import { Autocomplete, AutocompleteRenderInputParams, TextField } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { CustomPaper } from './CustomPaper';
import { OptionRenderer } from './OptionRenderer';
import { getAutocompleteSx } from './styles';
import { TagsRenderer } from './TagsRenderer';
import { OptionWithId, VyMultipleAutoCompleteProps } from './types';
import { useSelectAll } from './use-select-all';

export const VyMultipleAutoComplete = <OptionType extends OptionWithId>({
  options,
  value,
  onChange,
  getOptionLabel,
  label,
  placeholder,
  showSelectAll = false,
  size = 'small',
  sx,
  disabled,
  textFieldProps = {},
}: VyMultipleAutoCompleteProps<OptionType>) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const { allItemsSelected, optionsWithSelectAll, handleChange, displayValue } = useSelectAll({
    options,
    value,
    onChange,
    showSelectAll,
  });

  const handleInputChange = useCallback((_: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  const renderTags = useCallback(
    (tagValue: (OptionType | { id: '__SELECT_ALL__'; name: string })[]) => (
      <TagsRenderer
        tagValue={tagValue}
        isExpanded={isExpanded}
        onExpandToggle={() => setIsExpanded(true)}
        getOptionLabel={getOptionLabel}
        onChange={onChange}
      />
    ),
    [isExpanded, getOptionLabel, onChange],
  );

  const renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: OptionType | { id: '__SELECT_ALL__'; name: string },
      { selected }: { selected: boolean },
    ) => (
      <OptionRenderer
        props={props}
        option={option}
        selected={selected}
        allItemsSelected={allItemsSelected}
        showSelectAll={showSelectAll}
      />
    ),
    [allItemsSelected, showSelectAll],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField {...params} {...textFieldProps} label={label} placeholder={placeholder} />
    ),
    [label, placeholder, textFieldProps],
  );

  return (
    <Autocomplete
      multiple
      size={size}
      disabled={disabled}
      disablePortal
      disableCloseOnSelect
      options={optionsWithSelectAll}
      getOptionLabel={(option) => option.name}
      value={displayValue}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      renderValue={renderTags}
      renderOption={renderOption}
      renderInput={renderInput}
      sx={getAutocompleteSx(size, sx)}
      slots={{
        paper: CustomPaper,
      }}
      slotProps={{
        paper: {
          inputValue,
          onInputChange: setInputValue,
        } as Record<string, unknown>,
      }}
    />
  );
};
